import { CommonModule } from '@angular/common';
import { Component, TemplateRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { User } from '@app/model/user';
import { UserService } from '@app/services';
import { Router, RouterModule } from '@angular/router';
import { LoadingComponent } from '@app/shared/loading/loading.component';
import { AlertComponent } from '@app/shared/alert/alert.component';
// Removed ng-bootstrap imports
import { debounceTime, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupabaseStorageService } from '@app/services';
import { IdentityApply } from '@app/model/identity-apply';
import { IdentityApplyService } from '@app/services';
import { NotificationService } from '@app/services';
import { CategoryService } from '@app/services';
import { isPlatformBrowser } from '@angular/common';
import { LocalStorageService } from '@app/services';

@Component({
    selector: 'app-regis-page',
    imports: [FormsModule, CommonModule, LoadingComponent, RouterModule, AlertComponent],
    templateUrl: './regis-page.component.html'
})
export class RegisPageComponent {

  user: User = new User('', '', '', '', '', '', '', 0, 0, 0, '', false, false, false, 0, '', '', '');
  identity_apply_document: IdentityApply = new IdentityApply('', '', new Date(), '', '');
  name: string = '';
  email: string = '';
  password: string = '';
  confirmPassword: string = '';
  phone: string = '';
  address: string = '';
  error = false;
  failMessage = '';
  staticAlertClosed = false;
  isLoading = false;
  showTerms = false;
  showPrivacy = false;
  selectedFile: File | null = null;
  fontSize: number = 18;
  showPassword = false;
  showConfirmPassword = false;

  private _failMessage$ = new Subject<string>();

  @ViewChild('regisSuccessContent', { static: true }) regisSuccessTemplate!: TemplateRef<any>;
  // Removed NgbAlert ViewChild
  showModal: boolean = false;
  showRegisSuccessModal: boolean = false;
  showTermsModal: boolean = false;
  showPrivacyModal: boolean = false;

  constructor(
    private userService: UserService,
    private router: Router,
    private categoryService: CategoryService,
    // Removed NgbModal,
    private supabaseStorageService: SupabaseStorageService,
    private identityApplyService: IdentityApplyService,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private localStorageService: LocalStorageService
  ) {
    this._failMessage$
      .pipe(
        takeUntilDestroyed(),
        tap((message) => {
          this.failMessage = message;
          this.staticAlertClosed = false;
        }),
        debounceTime(5000) // 延長顯示時間到 5 秒
      )
      .subscribe(() => {
        this.staticAlertClosed = true;
        this.failMessage = '';
      });
  }

  /**
   * 格式化並驗證台灣手機號碼
   * @param phone 輸入的手機號碼
   * @returns 格式化後的國際格式手機號碼，或 null（如果格式無效）
   */
  private formatPhoneNumber(phone: string): string | null {
    const trimmedPhone = phone?.trim();
    if (!trimmedPhone || !/^\d+$/.test(trimmedPhone)) return null;

    const normalizedPhone = trimmedPhone;
    if (/^09\d{8}$/.test(normalizedPhone)) {
      return `+886${normalizedPhone.slice(1)}`;
    }

    return null;
  }

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.fontSize = this.localStorageService.getItem('fontSize', 18);
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  async register(data: User) {
    this.error = false;
    this.staticAlertClosed = false;
    this.failMessage = '';

    data.name = data.name?.trim();
    data.email = data.email?.trim().toLowerCase() || '';
    data.address = data.address?.trim();
    data.specialty_description = data.specialty_description?.trim() || '';
    data.certifications_description = data.certifications_description?.trim() || '';

    if (!data.name || !data.password || !data.phone || !data.address || !data.account_type) {
      this._failMessage$.next('請完整填寫所有必填欄位！');
      return;
    }

    if (data.name.length > 50) {
      this._failMessage$.next('姓名不得超過 50 個字元！');
      return;
    }

    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      this._failMessage$.next('請輸入有效的電子郵件格式！');
      return;
    }

    if (data.password.length < 6 || data.password.length > 72) {
      this._failMessage$.next('密碼長度需為 6 至 72 個字元！');
      return;
    }

    if (data.password !== this.confirmPassword) {
      this._failMessage$.next('兩次輸入的密碼不一致！');
      return;
    }

    const phoneNumber = this.formatPhoneNumber(data.phone);
    if (!phoneNumber) {
      this._failMessage$.next('請輸入有效的台灣手機號碼（例：0912345678）');
      return;
    }

    if (data.address.length > 200) {
      this._failMessage$.next('地址不得超過 200 個字元！');
      return;
    }

    if (!['personal', 'organization'].includes(data.account_type)) {
      this._failMessage$.next('請選擇有效的帳號類別！');
      return;
    }

    data.phone = phoneNumber;
    this.isLoading = true;

    try {
      const res = await this.userService.signUp(phoneNumber, data.password);
      if (res?.error) {
        if (res.error.code === 'weak_password') {
          this._failMessage$.next(`密碼長度不得少於6個字元！`);
        } else if (res.error.code === 'validation_failed') {
          this._failMessage$.next(`Email格式錯誤！`);
        } else if (res.error.code === 'user_already_exists') {
          this._failMessage$.next(`此帳號已存在！`);
        } else {
          this._failMessage$.next(`註冊失敗：${res.error.message || '未知錯誤'}`);
        }
        this.error = true;
        this.isLoading = false;
        this.staticAlertClosed = false;
        return;
      }
      await this.userService.regis(data);
      await this.categoryService.joinOpenCategories();

      if (this.selectedFile) {
        // 證件放在使用者自己的資料夾，檔名不可預測，只有用戶管理員能讀取
        const fileName = `${res.data.user?.id}/${crypto.randomUUID()}`;
        const { error, publicUrl } = await this.supabaseStorageService.uploadIdentityDocument(this.selectedFile, fileName);
        if (error) {
          console.error('上傳身分證件失敗', error);
          this._failMessage$.next('上傳身分證件失敗，請稍後再試！');
          this.isLoading = false;
          this.staticAlertClosed = false;
          return;
        } else {
          this.identity_apply_document = new IdentityApply('', '', new Date(), '', publicUrl);
          await this.identityApplyService.createIdentityApply(this.identity_apply_document);
        }
      } else {
        this.identity_apply_document = new IdentityApply('', '', new Date(), '', '');
        await this.identityApplyService.createIdentityApply(this.identity_apply_document);
      }
      this.showRegisSuccessModal = true;
      
      // 獲取具有用戶管理權限的管理員 ID 列表
      const adminIds = await this.userService.getUserAdminsId();
      
      // 發送通知給所有有權限的管理員
      if (adminIds && adminIds.length > 0) {
        if (data.id != null) {
          // 如果有 user ID，發送帶有 ID 的通知
          for (const adminId of adminIds) {
            await this.notificationService.tryCreateNotification(
              '待審核註冊資料通知', 
              `有一筆新的註冊資料待審核，請儘速審合，謝謝。`, 
              adminId, 
              'identity_apply', 
              `${data.id}`
            );
          }
        } else {
          // 如果沒有 user ID，發送不帶 ID 的通知
          for (const adminId of adminIds) {
            await this.notificationService.tryCreateNotification(
              '待審核註冊資料通知', 
              `有一筆新的註冊資料待審核，請儘速審合，謝謝。`, 
              adminId, 
              'identity_apply', 
              ''
            );
          }
        }
      }
    } catch (error) {
      console.error('註冊失敗', error);
      this._failMessage$.next('註冊過程中出現錯誤，請稍後再試！');
      this.isLoading = false;
      this.staticAlertClosed = false;
    }
  }

  async closeRegisSuccessModal() {
    this.showRegisSuccessModal = false;
    await this.userService.signOut();
    this.isLoading = false;
    // 修復：使用正確的路由路徑
    await this.router.navigate(['/login']);
  }

  async onFileSelected(event: any): Promise<void> {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        console.error('檔案格式不符合要求');
        this.staticAlertClosed = false;
        this._failMessage$.next('僅接受 JPEG、PNG、HEIC 或 PDF 檔案格式！');
        event.target.value = ''; // 重置 input
        return;
      } else if (file.size > 4 * 1024 * 1024) {
        console.error('檔案大小超過 4MB');
        this.staticAlertClosed = false;
        this._failMessage$.next('檔案大小超過 4MB，請重新選擇！');
        event.target.value = ''; // 重置 input
        return;
      }
      if (file.type === 'image/heic') {
        try {
          const { default: heic2any } = await import('heic2any');
          const convertedBlobs = await heic2any({ blob: file, toType: 'image/png' });
          const convertedBlob = Array.isArray(convertedBlobs) ? convertedBlobs[0] : convertedBlobs;
          if (!(convertedBlob instanceof Blob)) {
            throw new Error('轉換結果不是 Blob');
          }
          const convertedFile = new File([convertedBlob], file.name.replace('.heic', '.png'), {
            type: 'image/png',
          });
          this.selectedFile = convertedFile;
        } catch (error) {
          this.staticAlertClosed = false;
          this._failMessage$.next('HEIC 檔案轉換失敗，請嘗試其他格式！');
          event.target.value = ''; // 重置 input
        }
      } else {
        this.selectedFile = file;
      }
    } else {
      this.selectedFile = null;
    }
  }

  openTermsModal() {
    this.showTermsModal = true;
  }

  closeTermsModal() {
    this.showTermsModal = false;
  }

  openPrivacyModal() {
    this.showPrivacyModal = true;
  }

  closePrivacyModal() {
    this.showPrivacyModal = false;
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
}
